import { z } from 'zod';
import { canonicalDec, isDecimalText } from '../fuel/decimal.js';
import { toIssues } from '../materials/catalog.js';
import { RA_SHIP_GROUPS } from '../risk/master.js';
import { ownerReferenceSchema } from '../voyage/reference.js';
import type { ParseResult } from '../voyage/schemas.js';

/**
 * Gemi özellikleri (plan M7 Task 4, 6c; `gemi-pms-ekipman.jpeg` üst kartı): Vessel Type, Built,
 * Flag, Class, Class No, Call Sign. Şirket verisidir; yalnız bulutta, `settings.edit` ile
 * yazılır ve gemiye iner (ADR 0029 §2).
 */

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');

const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);

/** İlk yapım yılı sınırı: daha eskisi kayıt hatasıdır. */
const FIRST_YEAR = 1900;

function particularsSchema(year: number) {
  return z.strictObject({
    vesselType: optionalText(60),
    builtYear: z
      .number(typed('INVALID_NUMBER'))
      .int('INVALID_NUMBER')
      .min(FIRST_YEAR, 'OUT_OF_RANGE')
      // Yapım yılı gelecekte olamaz (CLAUDE.md "Zaman")
      .max(year, 'FUTURE_DATE')
      .nullish()
      .transform((value) => value ?? null),
    flag: optionalText(60),
    classSociety: optionalText(60),
    classNo: optionalText(40),
    callSign: optionalText(20),
    // Risk ana verisinin gemi grubu (ADR 0086 §4): boşsa gemiye yalnız ALL uygulanır
    raGroup: z
      .enum(RA_SHIP_GROUPS, { error: 'INVALID' })
      .nullish()
      .transform((value) => value ?? null),
    /**
     * Charter'sız seferin performans referansı (ADR 0084 Ek 2): geminin varsayılan Owner's
     * reference'ı; seferde alan alan üzerine yazılır. Boşsa referans yok.
     */
    ownerReference: ownerReferenceSchema
      .nullish()
      .transform((value) => (value && Object.values(value).some((v) => v !== null) ? value : null)),
    /**
     * Gross tonnage (ADR 0117 §8): MRV/ETS uygunluk önerisinin girdisi; kanonik ondalık metin,
     * sıfırdan büyük. Boş: bilinmiyor (uygunluk "değerlendirilmedi").
     */
    grossTonnage: z
      .string(typed('INVALID_DECIMAL'))
      .refine(isDecimalText, { message: 'INVALID_DECIMAL', abort: true })
      .transform(canonicalDec)
      .refine((value) => !value.startsWith('-') && value !== '0', 'OUT_OF_RANGE')
      .nullish()
      .transform((value) => value ?? null),
    /** ISM sorumluluğunu üstlenen şirket (DoC sahibi; ETS'de "shipping company"; ADR 0117 §8). */
    ismCompany: optionalText(120),
  });
}

export type VesselParticulars = z.output<ReturnType<typeof particularsSchema>>;

/** Gemi özelliklerini ayrıştırır; `year` sunucunun yılıdır (yapım yılı gelecekte olamaz). */
export function parseVesselParticulars(
  input: unknown,
  options: { year: number },
): ParseResult<VesselParticulars> {
  const result = particularsSchema(options.year).safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}
